import React, { FC, ReactNode, useCallback, useEffect } from 'react'
import { Accordion as BaseAccordion, AccordionItem } from '@overdose/components'
import {
  IconCheckbox,
  IconCircleCheck,
  IconMarqueeOff,
} from '@tabler/icons-react'
import classNames from 'classnames'
import Link from 'next/link'
import { Image } from '~/components/Image'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { Button } from '../Button'
import RichText from '../RichText/RichText'
import { VideoPlayer } from '../VideoPlayer'
import styles from './ProjectStepsAccordion.module.css'
import {
  ProjectStep,
  ProjectStepsAccordionProps,
  ProjectStepContent,
  AnchorContent,
} from './ProjectStepsAccordion.types'

const EVEN_ROW = 2
const STYLE_MX_AUTO_ROUNDED = 'mx-auto rounded-md'

const renderTypography = (text: string, isBold: boolean, key?: number) => {
  return (
    <Typography
      key={key}
      tag={TypographyTag.p}
      className='!leading-[24px]'
      variant={
        isBold
          ? TypographyVariant.BodyRegularBold
          : TypographyVariant.BodyRegular
      }>
      {text}
    </Typography>
  )
}

const renderAnchor = (child: AnchorContent, idx: number) => {
  return (
    <Link
      key={idx}
      href={child.attrs.url}
      target={child.attrs.target}
      className={`${classNames('underline')} ${styles.link}`}>
      {renderTypography(child.children[0].text, child.children[0]?.bold)}
    </Link>
  )
}

const renderTextContent = (content: ProjectStepContent) => {
  return content.children?.map((child, idx: number) => {
    return child.type === 'a'
      ? renderAnchor(child, idx)
      : renderTypography(child.text, child.bold, idx)
  })
}

const parseStepContent = {
  p: (content: ProjectStepContent, index: number) => {
    return <div key={index}>{renderTextContent(content)}</div>
  },

  li: (content: ProjectStepContent, index: number) => {
    return <li key={index}>{renderTextContent(content)}</li>
  },

  heading: (content: ProjectStepContent, index: number) => {
    return (
      <Typography
        key={index}
        className={classNames('-mb-6')}
        tag={TypographyTag.p}
        variant={TypographyVariant.BodyRegularBold}>
        {content.content.heading}
      </Typography>
    )
  },

  image: (content: ProjectStepContent, index: number) => {
    const { src, altText, width, height } = content.content.image

    return (
      <Image
        key={index}
        className={classNames(STYLE_MX_AUTO_ROUNDED)}
        src={src}
        alt={altText}
        width={width}
        height={height}
        loading='eager'
      />
    )
  },

  video: (content: ProjectStepContent, index: number) => {
    return (
      <div key={index} className={classNames(styles.video, 'mx-auto')}>
        <VideoPlayer
          source={content.content.video}
          muted={false}
          autoplay={false}
        />
      </div>
    )
  },
}

type DynamicTagProps = {
  tagName: keyof React.JSX.IntrinsicElements
  children?: ReactNode
  [prop: string]: unknown
}
const DynamicTag: FC<DynamicTagProps> = ({ tagName, children, ...props }) => {
  return React.createElement(tagName, props, children)
}

type CodeBlockType = {
  stepContent: ProjectStepContent
}
const CodeBlock: FC<CodeBlockType> = ({ stepContent }) => {
  return (
    <div className={styles.codeBlockContainer}>
      <pre className={styles.codeBlock}>
        {stepContent.content.code.code_block_contents
          .split('\n')
          .map((line, idx) => {
            return (
              <div key={idx} className={styles.codeLine}>
                <span className={styles.lineNumber}>{`${idx + 1}  `}</span>
                <code>{line}</code>
              </div>
            )
          })}
      </pre>
    </div>
  )
}

const ProjectStepsAccordion = ({
  completedSteps,
  steps,
  className,
  setStepCompleteStatus,
}: ProjectStepsAccordionProps) => {
  const renderStepContent = useCallback(
    (stepContent: ProjectStepContent, index: number) => {
      if (!stepContent) {
        return null
      }

      const renderChildren = stepContent.children?.map((child, idx) => {
        return renderStepContent(child, idx)
      })

      switch (stepContent.type) {
        case 'p':
        case 'heading':
        case 'image':
        case 'video':
        case 'li':
          return parseStepContent[stepContent.type](stepContent, index)

        case 'thead':
        case 'tbody':
          return (
            <DynamicTag key={index} tagName={stepContent.type}>
              {renderChildren}
            </DynamicTag>
          )

        case 'th':
        case 'td':
          return (
            <DynamicTag
              key={index}
              tagName={stepContent.type}
              className={classNames(
                styles[stepContent.type],
                'text-left',
                'p-2'
              )}>
              {renderChildren}
            </DynamicTag>
          )

        case 'table':
          return (
            <div key={index} className='my-4'>
              <table className={classNames('min-w-full', styles.table)}>
                {renderChildren}
              </table>
            </div>
          )

        case 'tr':
          return (
            <tr
              key={index}
              className={classNames(
                index % EVEN_ROW === 0 ? styles.evenRow : styles.oddRow,
                'border-b'
              )}>
              {renderChildren}
            </tr>
          )

        case 'code':
          return <CodeBlock key={index} stepContent={stepContent} />

        case 'ul':
          return <ul className='p-[revert]'>{renderChildren}</ul>

        case 'ol':
          return <ol className='p-[revert]'>{renderChildren}</ol>

        default:
          return renderChildren
      }
    },
    []
  )

  // Recalculate AccordionItem heights after dynamic content has loaded
  useEffect(() => {
    const triggerResize = () => {
      window.dispatchEvent(new Event('resize'))
    }

    // Listen for dynamic content load events
    const dynamicElements = document.querySelectorAll('img, video')
    dynamicElements.forEach((el) => {
      el.addEventListener('load', triggerResize)
      el.addEventListener('error', triggerResize)
    })

    return () => {
      dynamicElements.forEach((el) => {
        el.removeEventListener('load', triggerResize)
        el.removeEventListener('error', triggerResize)
      })
    }
  }, [])

  return (
    <div className={classNames(styles.root, className)}>
      <BaseAccordion multiple>
        {steps.length > 0 &&
          steps.map((step: ProjectStep, index: number) => {
            const stepCompleted = completedSteps?.includes(step.stepId)

            return (
              <div key={index} className='pb-2 pt-2'>
                <AccordionItem
                  name={`${step.title}-${index}`}
                  header={
                    <div
                      id={`${step.title}-${index}`}
                      className={classNames(
                        'flex justify-between pr-12 xs:pr-52 -ml-4'
                      )}>
                      <Typography
                        tag={TypographyTag.p}
                        variant={TypographyVariant.BodyXLargeBold}>
                        {step.title}
                      </Typography>
                      <div
                        className={classNames(
                          stepCompleted && styles.active,
                          styles.stepHeaderStatus,
                          'flex items-center pointer-events-none rounded-md'
                        )}>
                        <div
                          className={classNames(
                            styles.stepCompletedBadge,
                            'flex items-center rounded-md'
                          )}>
                          <IconCircleCheck size={12} />
                          <Typography
                            className={classNames('hidden xs:inline ml-1')}
                            tag={TypographyTag.span}
                            variant={TypographyVariant.BodySmallBold}>
                            Step Completed
                          </Typography>
                        </div>
                      </div>
                    </div>
                  }>
                  <div
                    className={classNames(
                      styles.stepContent,
                      'flex flex-col gap-8 pt-6 pb-10'
                    )}>
                    <RichText content={step.contentBlocks} />
                    <div className={classNames('self-center')}>
                      <Button
                        className='text-base py-2 px-3 normal-case'
                        size='md'
                        shape='square'
                        status={stepCompleted ? 'secondary' : 'primary'}
                        onClick={() => {
                          return setStepCompleteStatus(step.stepId)
                        }}
                        icon={
                          stepCompleted ? <IconMarqueeOff /> : <IconCheckbox />
                        }
                        style={{
                          '--btn-icon-size': '12px',
                        }}>
                        {stepCompleted
                          ? 'Mark step as incomplete'
                          : 'Mark step as complete'}
                      </Button>
                    </div>
                  </div>
                </AccordionItem>
              </div>
            )
          })}
      </BaseAccordion>
    </div>
  )
}

export default ProjectStepsAccordion
